/**
 * Leave Application Component
 * Allows users to apply for leave with balance checking
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Calendar, 
  Send, 
  AlertTriangle,
  CheckCircle,
  X,
  Loader2,
  Info,
  CalendarDays,
  Paperclip,
  LifeBuoy
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { parseCalendarDate } from '../../utils/formatters';
import { LeaveBalanceCards } from './leave/LeaveBalanceCards';
import { LeaveAssistPanel } from './leave/LeaveAssistPanel';

const API = process.env.REACT_APP_BACKEND_URL || '';

const EMPTY_FORM = { leave_type: 'casual', start_date: '', end_date: '', reason: '', day_part: 'full' };

// LEAVE-UI-001 (Sep 2026): premium presentation. Data flow, validation and submission logic are unchanged.
const LeaveApplication = ({ onSuccess, onClose, onPlanBetter, onOpenPolicy }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [holidays, setHolidays] = useState([]);
  const [docFile, setDocFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [blockWarning, setBlockWarning] = useState(null);
  const [checkingBlocks, setCheckingBlocks] = useState(false);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [balanceWarning, setBalanceWarning] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);

  // Fetch leave balances on mount
  useEffect(() => {
    const fetchBalances = async () => {
      try {
        const [balanceRes, typesRes, holidaysRes] = await Promise.all([
          axios.get(`${API}/api/leave-quota/my-balance`),
          axios.get(`${API}/api/leave-quota/types`),
          axios.get(`${API}/api/leave-quota/holidays`)
        ]);
        setLeaveBalances(balanceRes.data.balances || []);
        setLeaveTypes(typesRes.data.leave_types || []);
        setHolidays((holidaysRes.data.holidays || []).map(h => h.date));
      } catch (err) {
        console.error('Error fetching leave data:', err);
        toast.error('Could not load leave types and balances. Please refresh or contact HR if this persists.');
      }
    };
    fetchBalances();
  }, []);

  // Check for blocked dates when dates change
  useEffect(() => {
    const checkBlocks = async () => {
      if (!formData.start_date || !formData.end_date) {
        setBlockWarning(null);
        return;
      }

      try {
        setCheckingBlocks(true);
        const response = await axios.get(`${API}/api/leave/blocks/check`, {
          params: {
            start_date: formData.start_date,
            end_date: formData.end_date
          }
        });

        if (response.data.is_blocked) {
          setBlockWarning({
            dates: response.data.blocked_dates,
            reason: response.data.block_reason
          });
        } else {
          setBlockWarning(null);
        }
      } catch (err) {
        console.error('Error checking blocks:', err);
      } finally {
        setCheckingBlocks(false);
      }
    };

    checkBlocks();
  }, [formData.start_date, formData.end_date]);

  // Check balance when leave type or dates change
  useEffect(() => {
    const days = calculateDays();
    if (days <= 0) {
      setBalanceWarning(null);
      return;
    }

    const balance = leaveBalances.find(b => b.leave_type_id === formData.leave_type);
    if (balance) {
      if (balance.paid === false) {
        // Unpaid types have no balance limit
        setBalanceWarning(null);
      } else if (balance.available < days) {
        setBalanceWarning({
          type: 'insufficient',
          available: balance.available,
          requested: days
        });
      } else if (balance.available - days <= 2) {
        setBalanceWarning({
          type: 'low',
          remaining: balance.available - days
        });
      } else {
        setBalanceWarning(null);
      }
    }
  }, [formData.leave_type, formData.start_date, formData.end_date, formData.day_part, leaveBalances, holidays]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (blockWarning) {
      setError('Cannot submit leave application - selected dates are blocked');
      return;
    }

    // Check balance before submitting (paid types only)
    const days = calculateDays();
    const balance = leaveBalances.find(b => b.leave_type_id === formData.leave_type);
    if (balance && balance.paid !== false && balance.available < days) {
      setError(`Insufficient leave balance. Available: ${balance.available} days, Requested: ${days} days`);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const applyRes = await axios.post(`${API}/api/leave/apply`, formData);
      
      // Phase 2 — upload supporting document (optional)
      const newId = applyRes.data?.id || applyRes.data?.request?.id;
      if (docFile && newId) {
        try {
          const fd = new FormData();
          fd.append('file', docFile);
          await axios.post(`${API}/api/leave-registry/requests/${newId}/documents`, fd);
        } catch (docErr) {
          console.error('Document upload failed', docErr);
        }
      }
      
      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
        onClose?.();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit leave application');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDayPartChange = (val) => {
    setFormData(prev => ({
      ...prev,
      day_part: val,
      // Half-day locks end_date = start_date
      end_date: val !== 'full' ? (prev.start_date || prev.end_date) : prev.end_date
    }));
  };

  // Keep end_date synced to start_date whenever half-day is selected
  useEffect(() => {
    if (formData.day_part !== 'full' && formData.start_date && formData.end_date !== formData.start_date) {
      setFormData(prev => ({ ...prev, end_date: prev.start_date }));
    }
  }, [formData.start_date, formData.day_part]);

  const calculateDays = () => {
    if (!formData.start_date || !formData.end_date) return 0;
    const start = parseCalendarDate(formData.start_date);
    const end = parseCalendarDate(formData.end_date);
    if (!start || !end || end < start) return 0;
    // Working days only — excludes weekends + company holidays (P1)
    const holidaySet = new Set(holidays);
    let days = 0;
    const d = new Date(start);
    while (d <= end) {
      const dow = d.getDay();
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (dow !== 0 && dow !== 6 && !holidaySet.has(iso)) days += 1;
      d.setDate(d.getDate() + 1);
    }
    if (formData.day_part !== 'full' && days > 0) return 0.5;
    return days;
  };

  // Get current balance for selected leave type
  const getCurrentBalance = () => {
    return leaveBalances.find(b => b.leave_type_id === formData.leave_type);
  };

  const today = new Date().toISOString().split('T')[0];

  if (success) {
    return (
      <div className="lv-card an-pop text-center py-14 px-6" data-testid="leave-apply-success">
        <span className="mx-auto mb-4 h-16 w-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center"><CheckCircle className="w-9 h-9" /></span>
        <h3 className="text-xl font-bold text-slate-900 mb-1.5">Leave Application Submitted!</h3>
        <p className="text-slate-500">Your request has been sent for approval.</p>
      </div>
    );
  }

  const currentBalance = getCurrentBalance();
  const requestedDays = calculateDays();
  const remainingAfterRequest = currentBalance
    ? Math.round((currentBalance.available - requestedDays) * 10) / 10
    : 0;
  const submitDisabled = loading || !!blockWarning || balanceWarning?.type === 'insufficient';
  const handleCancel = () => { if (onClose) onClose(); else { setFormData(EMPTY_FORM); setDocFile(null); setError(null); } };
  const labelCls = 'block text-[13px] font-semibold text-slate-800 mb-1.5';
  const inputCls = 'lv-input w-full h-10 rounded-xl border border-slate-200 bg-white text-[14px] text-slate-800 placeholder:text-slate-400';

  return (
    <form onSubmit={handleSubmit} className="space-y-3.5" data-testid="leave-apply-form">
      {/* Leave type = balance cards (selection + exhausted rules unchanged) */}
      <LeaveBalanceCards balances={leaveBalances} selectedId={formData.leave_type}
        onSelect={(id) => setFormData(prev => ({ ...prev, leave_type: id }))} />

      <div className="lv-card an-rise p-5" style={{ animationDelay: '200ms' }} data-testid="leave-apply-card">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3.5">
            <span className="h-10 w-10 shrink-0 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center"><Send className="h-5 w-5" strokeWidth={2} /></span>
            <div>
              <h2 className="text-[18px] font-bold text-slate-900 tracking-tight leading-tight">Apply for Leave</h2>
              <p className="text-[12.5px] text-slate-500 mt-0.5">Fill in the details to submit your leave request.</p>
            </div>
          </div>
          {onOpenPolicy && (
            <button type="button" onClick={onOpenPolicy} data-testid="leave-need-help-btn"
              className="an-btn hidden sm:inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-blue-50 text-blue-700 text-[13px] font-semibold hover:bg-blue-100 border border-blue-100">
              <LifeBuoy className="h-4 w-4" /> Need Help?
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-6">
          <div className="lg:col-span-2 space-y-4">
            {/* Selected-type balance summary (LEAVE-REGISTRY-P1) */}
            {currentBalance && (
              <div className="rounded-xl border border-slate-200/80 bg-slate-50/70 px-4 py-3" data-testid="leave-balance-summary">
                <div className="flex items-center gap-2 mb-2.5">
                  <Info className="w-4 h-4 text-slate-500" />
                  <span className="text-[13px] font-semibold text-slate-700">{currentBalance.leave_type_name} Balance — {new Date().getFullYear()}</span>
                  {currentBalance.paid === false && <Badge variant="outline" className="text-[11px] text-slate-500 bg-white">Unpaid — no balance limit</Badge>}
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
                  {[['balance-entitled', currentBalance.total_quota, 'Entitled', 'text-slate-800'],
                    ['balance-available', currentBalance.available, 'Available', 'text-emerald-600'],
                    ['balance-used', currentBalance.used, 'Used', 'text-slate-800'],
                    ['balance-pending', currentBalance.pending, 'Pending Approval', 'text-amber-600'],
                    ['balance-carry-forward', currentBalance.carried_forward, 'Carry Forward', 'text-blue-600'],
                    ['balance-remaining-after', requestedDays > 0 ? remainingAfterRequest : '—', 'After This Request', remainingAfterRequest < 0 ? 'text-red-600' : 'text-slate-800']].map(([tid, val, lbl, cls]) => (
                    <div key={tid} className="rounded-lg bg-white/80 border border-slate-100 py-1.5">
                      <p className={`text-[15px] font-bold leading-tight ${cls}`} data-testid={tid}>{val}</p>
                      <p className="text-[10.5px] text-slate-500 leading-tight mt-0.5">{lbl}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {balanceWarning && (
              <div className={`an-pop p-3 rounded-xl flex items-start gap-2 border ${balanceWarning.type === 'insufficient' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                <AlertTriangle className={`w-5 h-5 mt-0.5 ${balanceWarning.type === 'insufficient' ? 'text-red-500' : 'text-amber-500'}`} />
                <div>
                  {balanceWarning.type === 'insufficient' ? (
                    <><p className="text-sm font-medium text-red-700">Insufficient Balance</p>
                      <p className="text-xs text-red-600">You have {balanceWarning.available} days available but requested {balanceWarning.requested} days.</p></>
                  ) : (
                    <><p className="text-sm font-medium text-amber-700">Low Balance Warning</p>
                      <p className="text-xs text-amber-600">You will have only {balanceWarning.remaining} days remaining after this request.</p></>
                  )}
                </div>
              </div>
            )}

            {/* Day Part — Full / Half day (LEAVE-REGISTRY-P1) */}
            {(currentBalance?.allow_half_day !== false) && (
              <div>
                <label className={labelCls}>Day Type</label>
                <div className="flex flex-wrap gap-2.5" role="radiogroup" aria-label="Day Type">
                  {[['full', 'Full Day'], ['half_am', 'Half Day (AM)'], ['half_pm', 'Half Day (PM)']].map(([val, label]) => (
                    <button key={val} type="button" role="radio" aria-checked={formData.day_part === val} data-testid={`day-part-${val}`} onClick={() => handleDayPartChange(val)}
                      className={`lv-seg h-9 px-4 rounded-xl border text-[14px] font-semibold ${formData.day_part === val ? 'is-active' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'}`}>
                      {label}
                    </button>
                  ))}
                </div>
                {formData.day_part !== 'full' && <p className="text-xs text-slate-500 mt-1.5">Half-day leave applies to a single date (0.5 day).</p>}
              </div>
            )}

            {/* Date Range */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className={labelCls} htmlFor="leave-start-date">Start Date</label>
                <div className="relative">
                  <CalendarDays className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input id="leave-start-date" type="date" name="start_date" data-testid="leave-start-date-input" value={formData.start_date} onChange={handleChange} min={today} required
                    className={`${inputCls} pl-10 pr-3`} />
                </div>
              </div>
              <div>
                <label className={labelCls} htmlFor="leave-end-date">End Date</label>
                <div className="relative">
                  <CalendarDays className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input id="leave-end-date" type="date" name="end_date" data-testid="leave-end-date-input" value={formData.end_date} onChange={handleChange} min={formData.start_date || today} required
                    disabled={formData.day_part !== 'full'}
                    className={`${inputCls} pl-10 pr-3 ${formData.day_part !== 'full' ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : ''}`} />
                </div>
              </div>
            </div>

            {formData.start_date && formData.end_date && (
              <div className="an-pop rounded-xl border border-indigo-100 bg-indigo-50/70 px-4 py-2.5 flex items-center gap-2.5 text-[13.5px]" data-testid="leave-days-count">
                <Calendar className="w-4.5 h-4.5 text-indigo-600" />
                <span className="text-indigo-900">Total: <strong>{calculateDays()} working day(s)</strong>
                  <span className="text-xs text-indigo-500 ml-2">(weekends &amp; company holidays excluded)</span></span>
                {checkingBlocks && <Loader2 className="w-4 h-4 text-indigo-600 animate-spin ml-auto" />}
              </div>
            )}

            {blockWarning && (
              <div className="an-pop bg-red-50 border border-red-200 rounded-xl p-4" data-testid="leave-block-warning">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-red-800">Leave Blocked</h4>
                    <p className="text-sm text-red-700 mt-1">Leave applications are blocked for these dates due to: <strong>{blockWarning.reason}</strong></p>
                    <p className="text-xs text-red-600 mt-2">Blocked dates: {blockWarning.dates.join(', ')}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Reason */}
            <div>
              <label className={labelCls} htmlFor="leave-reason">Reason for Leave</label>
              <div className="relative">
                <textarea id="leave-reason" name="reason" data-testid="leave-reason-input" value={formData.reason} onChange={handleChange} rows={3} required minLength={5} maxLength={500}
                  placeholder="Please enter the reason for your leave..."
                  className="lv-input w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pb-7 text-[14px] text-slate-800 placeholder:text-slate-400 resize-none" />
                <span className="absolute bottom-2.5 right-3.5 text-[11.5px] text-slate-400 tabular-nums" data-testid="leave-reason-counter">{formData.reason.length}/500</span>
              </div>
            </div>

            {/* Supporting Document (Phase 2) */}
            <div>
              <label className={labelCls} htmlFor="leave-document">Supporting Document <span className="text-slate-400 font-normal">(optional, max 1 MB)</span></label>
              <label htmlFor="leave-document" className="lv-input flex items-center gap-3 h-10 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 px-3.5 cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors">
                <Paperclip className="h-4 w-4 text-slate-500 shrink-0" />
                <span className="text-[13px] text-slate-600 truncate">{docFile ? `${docFile.name} (${Math.round(docFile.size / 1024)} KB)` : 'Attach a file (PDF, image or Word document)'}</span>
                <input id="leave-document" type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" data-testid="leave-document-input" onChange={(e) => setDocFile(e.target.files?.[0] || null)} className="sr-only" />
              </label>
            </div>

            {error && (
              <div className="an-pop bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2" data-testid="leave-apply-error">
                <X className="w-5 h-5 text-red-600" />
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            )}

            <div className="flex items-center justify-between gap-3 pt-1">
              <button type="button" onClick={handleCancel} data-testid="leave-cancel-btn"
                className="an-btn h-10 px-5 rounded-xl border border-slate-200 bg-white text-[14px] font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-300">
                Cancel
              </button>
              <button type="submit" disabled={submitDisabled} data-testid="leave-submit-btn" aria-busy={loading}
                className={`an-btn an-btn-primary inline-flex items-center gap-2 h-10 px-5 rounded-xl text-[14px] font-semibold ${submitDisabled ? 'opacity-50 saturate-50 cursor-not-allowed' : ''}`}>
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <><Send className="w-4 h-4" /> Submit Leave Request</>}
              </button>
            </div>
          </div>

          <LeaveAssistPanel onPlanBetter={onPlanBetter} onOpenPolicy={onOpenPolicy} />
        </div>
      </div>
    </form>
  );
};

export default LeaveApplication;

